import { helpCenterSearchSrc4Evid628 } from '@wix/bi-logger-customer-care-data/v2';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';

import { pushUrl } from '../components/InnerLink';
import { PageType } from '../types';
import { useBI } from './useBI';
import useIsSearchRedirectToContactEnabled from './useIsSearchRedirectToContactEnabled';
import { useIsSitePickerEnabled } from './useIsSitePickerEnabled';
import { mapPageTypeToSourceName } from '../utils/bi';
import { useStore } from '../zustand/stores';

const CONTACT_PAGE_URL = 'https://www.wix.com/contact';

export type GoToSearchResultsPageParams = {
  term: string;
  previousPageType: PageType;
  overrideTerm?: string;
  searchSource?: 'header' | 'page';
};

type QueryParams = {
  term: string;
  previousPageType: PageType;
  overrideTerm?: string;
};

export const useGoToSearchResultsPage = () => {
  const { locale } = useRouter();
  const siteSelectorData = useStore((state) => state.siteSelectorSlice);
  const setSearchSessionId = useStore((state) => state.biSlice.setSearchSessionId);
  const isSitePickerEnabled = useIsSitePickerEnabled();
  const editorType = isSitePickerEnabled ? siteSelectorData?.selectedSite?.editor?.editorType : '';
  const isSearchRedirectToContactEnabled = useIsSearchRedirectToContactEnabled();
  const hasActiveLiveChatSession = useStore((state) => state.userSlice.hasActiveLiveChatSession);
  const itemId = useStore((state) => state.pageSlice.itemId);
  const { sendBIEvent } = useBI();

  const goToSearchResultsPage = async ({ term, previousPageType, overrideTerm = '', searchSource }: GoToSearchResultsPageParams) => {
    const effectiveTerm = (overrideTerm || term).trim();
    if (isSearchRedirectToContactEnabled && effectiveTerm) {
      await void sendBIEvent(
        helpCenterSearchSrc4Evid628({
          question: effectiveTerm,
          item_id: itemId ?? undefined,
          source_name: mapPageTypeToSourceName(previousPageType),
          kb_lang: locale as string,
          search_source: searchSource ?? 'page',
          input_type: overrideTerm ? 'suggested question' : 'input field',
        }),
      );
      if (!hasActiveLiveChatSession) {
        const metaSiteId = siteSelectorData?.selectedSite?.metaSiteId;
        const metaSiteIdParam = metaSiteId ? `&metaSiteId=${encodeURIComponent(metaSiteId)}` : '';
        window.location.assign(
          `${CONTACT_PAGE_URL}?query=${encodeURIComponent(effectiveTerm)}&referral=hc_search${metaSiteIdParam}`,
        );
        return;
      }
    }

    setSearchSessionId(uuidv4());

    const queryParams: QueryParams = { term: term.trim(), previousPageType };

    if (overrideTerm) {
      Object.assign(queryParams, { inputType: 'suggested question', term: overrideTerm.trim() });
    }

    if (editorType) {
      Object.assign(queryParams, { editorType });
    }

    await pushUrl({
      pathname: `/${locale}/search`,
      query: queryParams,
    });
  };

  return { goToSearchResultsPage };
};
